import { Divider, Form } from 'antd';
import { PageProps } from '@/types/PageProps';
import { DefaultRouteTabs } from '@/components/_common';
import CommissionByMovimentsForm from '@/components/Reports/CommissionReports/CommissionByMovimentsForm';
import { Helmet } from 'react-helmet';
import { useIntl } from 'react-intl';
import ButtonsToGenerateReports from '@/components/Reports/ButtonsToGenerateReports';
import { useReportGenerate } from '@/hooks';
import useFileLoading, { FileType } from '@/hooks/useFileLoading';
import { ReportCallback } from '@/hooks/useReportGenerate';
import { useQuestReportCommissionByMovement } from '@/core/domain/questReport/Reports/CommssionByMovement/CommissionByFinancial.hooks';
import {
  CommissionByMovementFormValues,
  CommissionByMovementOsStatusType,
  QuestCommissionByMovementRequest,
} from '@/core/domain/questReport/Reports/CommssionByMovement/CommissionByMovement.types';
import { DateTimeFormat } from '@/utils/moment';
import { useCommisionByMovementCsv } from '@/core/domain/questReport/CSV/Commission/CommissionByMoviment/CommissionByMoviment.hooks';

const CommissionByMovimentsPage = ({ route }: PageProps) => {
  const intl = useIntl();
  const [form] = Form.useForm<CommissionByMovementFormValues>();

  const { setFileLoading, setFileUnloading, fileLoading } = useFileLoading();
  const report = useReportGenerate();

  const generateReport = useQuestReportCommissionByMovement();
  const generateReportCsv = useCommisionByMovementCsv();

  const handleGenerateReport = async (
    format: FileType,
    callback?: ReportCallback,
  ) => {
    setFileLoading(format);

    try {
      const { initialDate, finalDate, osStatus, osType, ...values } =
        await form.validateFields();

      const obj: QuestCommissionByMovementRequest = {
        ...values,
        openFilter: osStatus.includes(CommissionByMovementOsStatusType.open),
        authorizationFilter: osStatus.includes(
          CommissionByMovementOsStatusType.authorized,
        ),
        closedFilter: osStatus.includes(
          CommissionByMovementOsStatusType.closed,
        ),
        osTypes: osType?.toString(),
        companiesIds: values.companiesIds?.toString(),
        employeesIds: values.employeesIds?.toString(),
        groupIds: values.groupIds?.toString(),
        startDate: initialDate.format(DateTimeFormat),
        endDate: finalDate.format(DateTimeFormat),
      };

      const result =
        format === 'PDF' || format === 'REPORT_VIEW'
          ? await generateReport.mutateAsync(obj)
          : await generateReportCsv.mutateAsync(obj);

      if (callback) {
        callback(result);
      }
    } catch {}

    setFileUnloading(format);
  };

  const handleView = () => {
    handleGenerateReport('REPORT_VIEW', (result) =>
      report.openFile({
        result,
        fileName: 'relatorio_comissao_por_movimentacao.pdf',
        fileType: 'application/pdf',
      }),
    );
  };

  const handlePdf = () => {
    handleGenerateReport('PDF', (result) =>
      report.downloadFile({
        result,
        fileName: 'relatorio_comissao_por_movimentacao.pdf',
      }),
    );
  };

  const handleCsv = () => {
    handleGenerateReport('CSV', (result) =>
      report.downloadFile({
        result,
        fileName: 'relatorio_comissao_por_movimentacao.csv',
      }),
    );
  };

  return (
    <DefaultRouteTabs route={route.parent} permissions={route.permissions}>
      <Helmet title={intl.formatMessage({ id: route.name })} />

      <CommissionByMovimentsForm form={form} />

      <Divider />

      <ButtonsToGenerateReports
        viewReport={handleView}
        generatePdf={handlePdf}
        generateCsv={handleCsv}
        loading={fileLoading}
      />
    </DefaultRouteTabs>
  );
};

export default CommissionByMovimentsPage;
